<script setup>
import AdvancedChartManager from '@/views/apps/chart/list/AdvancedChartManager.vue'

definePage({
  meta: {
    action: 'read',
    subject: 'bots',
    useBotNav: true,
  },
})

import { useRoute } from 'vue-router'

// Get the bot ID from the route params
const route = useRoute()
const botId = Number.parseInt(route.params.id)
</script>

<template>
  <AdvancedChartManager :bot-id="botId" :show-bot-filter="false" />

  <div class="container">
        <div class="header">
            <h1>گزارش روزانه مالی</h1>
            <div class="date-time">دوشنبه ۱۸ آذر ۱۴۰۳</div>
        </div>
        
        <div class="greeting">مدیر محترم، سلام و وقت بخیر</div>
        <p>خلاصه وضعیت مالی امروز شرکت را به شرح زیر تقدیم می‌نمایم:</p>
        
        <div class="section">
            <h3><span class="icon">📊</span> شاخص‌های کلیدی امروز</h3>
            
            <h4>وضعیت نقدینگی:</h4>
            <div class="kpi-grid">
                <div class="kpi-card">
                    <div class="kpi-title">موجودی نقد</div>
                    <div class="kpi-value">۴۲۵ میلیون ریال</div>
                    <div class="kpi-change negative">۱۲% کاهش نسبت به دیروز</div>
                </div>
                <div class="kpi-card">
                    <div class="kpi-title">دریافتی‌های امروز</div>
                    <div class="kpi-value">۸۷۰ میلیون ریال</div>
                </div>
                <div class="kpi-card">
                    <div class="kpi-title">پرداختی‌های امروز</div>
                    <div class="kpi-value">۱,۲۴۵ میلیون ریال</div>
                </div>
            </div>
            
            <div class="alert alert-warning">
                <span class="icon">⚠️</span>
                <strong>هشدار:</strong> خروج نقدینگی بیش از ورودی (منفی ۳۷۵ میلیون)
            </div>
            
            <h4>نسبت‌های مالی:</h4>
            <div class="list-item">
                <span>نسبت جاری: <strong>۱.۸</strong></span>
                <span class="badge badge-success">وضعیت مطلوب</span>
            </div>
            <div class="list-item">
                <span>نسبت آنی: <strong>۱.۲</strong></span>
                <span class="badge badge-success">در حد استاندارد</span>
            </div>
            <div class="list-item">
                <span>نسبت بدهی به دارایی: <strong>۴۳%</strong></span>
                <span class="badge badge-warning">۲% افزایش نسبت به هفته قبل</span>
            </div>
        </div>
        
        <div class="section">
            <h3><span class="icon">📈</span> عملکرد فروش</h3>
            <div class="kpi-grid">
                <div class="kpi-card">
                    <div class="kpi-title">فروش امروز</div>
                    <div class="kpi-value">۲,۳۴۰ میلیون ریال</div>
                    <div class="kpi-change positive">+۱۵% نسبت به میانگین ماهانه</div>
                </div>
                <div class="kpi-card">
                    <div class="kpi-title">تعداد فاکتور صادره</div>
                    <div class="kpi-value">۴۷ فقره</div>
                </div>
                <div class="kpi-card">
                    <div class="kpi-title">بزرگترین فروش امروز</div>
                    <div class="kpi-value">۳۸۵ میلیون ریال</div>
                    <div class="kpi-change">شرکت پارس صنعت</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <h3><span class="icon">💰</span> وضعیت هزینه‌ها</h3>
            <div class="kpi-card">
                <div class="kpi-title">کل هزینه‌های امروز</div>
                <div class="kpi-value">۱,۸۹۰ میلیون ریال</div>
            </div>
            
            <h4>سهم عمده هزینه‌ها:</h4>
            <div class="list-item">
                <span>خرید مواد اولیه</span>
                <div class="progress-bar" style="width: 45%;">
                    <div class="progress-fill" style="width: 100%;">۴۵%</div>
                </div>
            </div>
            <div class="list-item">
                <span>حقوق و دستمزد</span>
                <div class="progress-bar" style="width: 45%;">
                    <div class="progress-fill" style="width: 62%;">۲۸%</div>
                </div>
            </div>
            <div class="list-item">
                <span>هزینه‌های عملیاتی</span>
                <div class="progress-bar" style="width: 45%;">
                    <div class="progress-fill" style="width: 40%;">۱۸%</div>
                </div>
            </div>
            <div class="list-item">
                <span>سایر</span>
                <div class="progress-bar" style="width: 45%;">
                    <div class="progress-fill" style="width: 20%;">۹%</div>
                </div>
            </div>
        </div>
        
        <div class="section">
            <h3><span class="icon">🚨</span> نکات قابل توجه</h3>
            
            <h4>موارد بحرانی:</h4>
            <div class="alert alert-danger">
                ۳ فاکتور معوق بیش از ۹۰ روز به مبلغ ۸۲۰ میلیون ریال
            </div>
            <div class="alert alert-warning">
                هزینه‌های انرژی ۳۵% بالاتر از بودجه در این ماه
            </div>
            <div class="alert alert-danger">
                چک برگشتی: ۲۴۵ میلیون ریال از شرکت نوین تجارت
            </div>
            
            <h4>فرصت‌ها:</h4>
            <div class="list-item">
                <span>پیش‌پرداخت ۳۰۰ میلیونی از مشتری جدید</span>
                <span class="badge badge-success">مثبت</span>
            </div>
            <div class="list-item">
                <span>کاهش ۱۵% در هزینه‌های اداری نسبت به ماه قبل</span>
                <span class="badge badge-success">مثبت</span>
            </div>
            <div class="list-item">
                <span>افزایش ۲۰% در سفارشات آنلاین</span>
                <span class="badge badge-success">مثبت</span>
            </div>
        </div>
        
        <div class="actions">
            <h3><span class="icon">📋</span> اقدامات پیشنهادی</h3>
            <div class="action-item">
                <span class="action-label">فوری:</span> پیگیری وصول مطالبات معوق (اولویت: شرکت آریا پخش - ۳۴۵ میلیون)
            </div>
            <div class="action-item">
                <span class="action-label">مهم:</span> بررسی دلایل افزایش مصرف انرژی در خط تولید ۲
            </div>
            <div class="action-item">
                <span class="action-label">برنامه‌ریزی:</span> تنظیم سفارش مواد اولیه با توجه به کاهش نقدینگی
            </div>
        </div>
        
        <div class="section">
            <h3><span class="icon">🎯</span> پیش‌بینی هفته جاری</h3>
            <div class="list-item">
                <span>احتمال رسیدن به هدف فروش ماهانه</span>
                <div class="progress-bar" style="width: 200px;">
                    <div class="progress-fill" style="width: 78%;">۷۸%</div>
                </div>
            </div>
            <div class="list-item">
                <span>پیش‌بینی جریان نقدی</span>
                <span class="badge badge-success">مثبت</span>
            </div>
            <div class="list-item">
                <span>نیاز به تسهیلات</span>
                <span class="badge badge-success">منتفی</span>
            </div>
        </div>
        
        <div class="section">
            <h3><span class="icon">✅</span> دستاوردهای امروز</h3>
            <div class="list-item">
                <span>عقد قرارداد جدید با شرکت فولاد امید</span>
                <span class="badge badge-success">۱,۲۰۰ میلیون ریال</span>
            </div>
            <div class="list-item">
                <span>کاهش زمان تحویل محصولات</span>
                <span class="badge badge-success">۴.۵ روز</span>
            </div>
            <div class="list-item">
                <span>رضایت مشتریان در نظرسنجی ماهانه</span>
                <span class="badge badge-success">۹۲%</span>
            </div>
        </div>
        
        <div class="footer">
            <p>این گزارش در ساعت ۱۸:۳۰ توسط سیستم فرادید تهیه شده است</p>
            <p><strong>حسابیار</strong> - دستیار هوشمند مالی شما</p>
        </div>
    </div>
</template>

<style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: 'Vazir', 'IRANSans', Tahoma, sans-serif;
            background-color: #f5f7fa;
            color: #2c3e50;
            line-height: 1.6;
        }
        
        .container {
            max-width: 900px;
            margin: 20px auto;
            padding: 20px;
            background: white;
            border-radius: 12px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.08);
        }
        
        .header {
            background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
            color: white;
            padding: 25px;
            border-radius: 12px 12px 0 0;
            margin: -20px -20px 20px;
            text-align: center;
        }
        
        .header h1 {
            font-size: 28px;
            margin-bottom: 10px;
        }
        
        .date-time {
            font-size: 16px;
            opacity: 0.9;
        }
        
        .greeting {
            font-size: 18px;
            color: #6c5ce7;
            margin: 20px 0;
            font-weight: 500;
        }
        
        .section {
            margin: 25px 0;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 8px;
            border-right: 4px solid #6c5ce7;
        }
        
        .section h3 {
            color: #6c5ce7;
            font-size: 20px;
            margin-bottom: 15px;
            display: flex;
            align-items: center;
        }
        
        .icon {
            margin-left: 8px;
            font-size: 24px;
        }
        
        .kpi-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 15px;
            margin: 15px 0;
        }
        
        .kpi-card {
            background: white;
            padding: 15px;
            border-radius: 8px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.05);
        }
        
        .kpi-title {
            font-size: 14px;
            color: #7f8c8d;
            margin-bottom: 5px;
        }
        
        .kpi-value {
            font-size: 22px;
            font-weight: bold;
            color: #2c3e50;
        }
        
        .kpi-change {
            font-size: 14px;
            margin-top: 5px;
        }
        
        .positive { color: #27ae60; }
        .negative { color: #e74c3c; }
        .warning { color: #f39c12; }
        
        .list-item {
            padding: 10px 15px;
            background: white;
            margin: 8px 0;
            border-radius: 6px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .badge {
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: 500;
        }
        
        .badge-danger {
            background: #ffe4e1;
            color: #e74c3c;
        }
        
        .badge-success {
            background: #e8f8f5;
            color: #27ae60;
        }
        
        .badge-warning {
            background: #fff4e6;
            color: #f39c12;
        }
        
        .alert {
            padding: 15px;
            border-radius: 8px;
            margin: 15px 0;
            display: flex;
            align-items: center;
        }
        
        .alert-warning {
            background: #fff4e6;
            color: #f39c12;
            border-right: 4px solid #f39c12;
        }
        
        .alert-danger {
            background: #ffe4e1;
            color: #e74c3c;
            border-right: 4px solid #e74c3c;
        }
        
        .actions {
            margin: 20px 0;
            padding: 15px;
            background: #f0f3ff;
            border-radius: 8px;
        }
        
        .action-item {
            padding: 10px 0;
            border-bottom: 1px solid #ddd;
        }
        
        .action-item:last-child {
            border-bottom: none;
        }
        
        .action-label {
            font-weight: bold;
            color: #6c5ce7;
            margin-left: 8px;
        }
        
        .footer {
            text-align: center;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #eee;
            color: #7f8c8d;
            font-size: 14px;
        }
        
        .chart-container {
            width: 100%;
            height: 200px;
            background: white;
            border-radius: 8px;
            margin: 15px 0;
            padding: 15px;
            position: relative;
        }
        
        .progress-bar {
            width: 100%;
            height: 20px;
            background: #ecf0f1;
            border-radius: 10px;
            overflow: hidden;
            margin: 10px 0;
        }
        
        .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #6c5ce7, #a29bfe);
            text-align: center;
            color: white;
            font-size: 12px;
            line-height: 20px;
        }
    </style>
